import { Component, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ClientInvoiceService } from '../../docs/client-invoice/client-invoice.service';
import { CustomerAccountService } from '../../customer-account/customer-account.service';
import { CustomerAccountOperationComponent } from '../../customer-account/customer-account-operation.component';
import { finalize } from 'rxjs/operators';
import { NotificationService } from 'src/app/services/notification.service';
import { SupportService } from 'src/app/support.service';
import {
  OrderUnavailableProductsPayload,
  OrderUnavailableProductsResult,
  OrderService
} from '../order.service';

export interface UnavailableProductsDialogData {
  orderId: number;
}

@Component({
  selector: 'app-unavailable-products-dialog',
  templateUrl: './unavailable-products-dialog.component.html',
  styleUrls: ['./unavailable-products-dialog.component.scss']
})
export class UnavailableProductsDialogComponent {
  loading = false;
  applying = false;
  preview?: OrderUnavailableProductsResult;

  form: UntypedFormGroup = this.fb.group({
    source_invoice_id: null,
    cancel_product_id: null,
    cancel_quan: 1,
    replacement_product_id: null,
    replacement_quan: 1,
    replacement_price: null,
    reason: ''
  });

  constructor(
    private fb: UntypedFormBuilder,
    private orderService: OrderService,
    private clientInvoices: ClientInvoiceService,
    private customerAccounts: CustomerAccountService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    public supportService: SupportService,
    private dialogRef: MatDialogRef<UnavailableProductsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UnavailableProductsDialogData
  ) {}

  previewReplacement() {
    const payload = this.buildPayload();
    if (!payload) return;

    this.loading = true;
    this.orderService
      .previewUnavailableProducts(this.data.orderId, payload)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(
        (preview) => {
          this.preview = preview;
        },
        (err) => {
          this.preview = undefined;
          this.notificationService.warning(err?.error?.message || 'Не удалось рассчитать замену');
        }
      );
  }

  applyReplacement() {
    const payload = this.buildPayload();
    if (!payload) return;

    this.applying = true;
    this.orderService
      .applyUnavailableProducts(this.data.orderId, payload)
      .pipe(finalize(() => (this.applying = false)))
      .subscribe(
        (result) => {
          this.notificationService.notice(result.requires_credit_allocation ? 'Замена проведена. Средства оставлены на балансе; оплату замены выберите отдельно.' : 'Замена позиции проведена');
          this.dialogRef.close(result);
          if (result.requires_credit_allocation && result.target_invoice_id) void this.offerAllocation(result.target_invoice_id);
        },
        (err) => {
          this.notificationService.warning(err?.error?.message || 'Не удалось провести замену');
        }
      );
  }

  private async offerAllocation(invoiceId: number) {
    try {
      const invoice = await firstValueFrom(this.clientInvoices.getOne(invoiceId));
      if (!invoice.client?.id || !invoice.org?.id) return;
      const summary = await firstValueFrom(this.customerAccounts.summary(invoice.client.id, invoice.org.id));
      this.dialog.open(CustomerAccountOperationComponent, { width: '760px', maxWidth: 'calc(100vw - 24px)', maxHeight: 'calc(100dvh - 24px)', autoFocus: false,
        data: { clientId: invoice.client.id, clientName: invoice.client.name, orgId: invoice.org.id, orgName: invoice.org.name, kind: 'ALLOCATE', invoiceId, summary } });
    } catch { this.notificationService.warning('Замена сохранена. Зачёт можно выполнить во взаиморасчётах клиента.'); }
  }

  private buildPayload(): OrderUnavailableProductsPayload | null {
    const value = this.form.value;
    const sourceInvoiceId = Number(value.source_invoice_id || 0);
    const cancelProductId = Number(value.cancel_product_id || 0);
    const cancelQuan = Number(value.cancel_quan || 0);
    if (!sourceInvoiceId || !cancelProductId || cancelQuan <= 0) {
      this.notificationService.warning('Укажите счет, товар и количество для отмены');
      return null;
    }

    const replacementProductId = Number(value.replacement_product_id || 0);
    const replacementQuan = Number(value.replacement_quan || 0);
    const replacementPrice = Number(value.replacement_price || 0);
    const replacementProducts =
      replacementProductId && replacementQuan > 0 && replacementPrice > 0
        ? [
            {
              product_id: replacementProductId,
              quan: replacementQuan,
              price: replacementPrice
            }
          ]
        : [];

    return {
      source_invoice_id: sourceInvoiceId,
      cancel_products: [
        {
          product_id: cancelProductId,
          quan: cancelQuan
        }
      ],
      replacement_products: replacementProducts,
      settlement_mode: 'CREDIT_TRANSFER',
      reason: String(value.reason || '').trim() || undefined
    };
  }
}
